/**
 * Exceptions Report — single entry point across every exception area.
 *
 * Two dropdowns at the top:
 *   - Area: Sales / Stock Management / Product Setup / Product Pricing / System
 *   - Exception Type: filtered by the selected Area
 *
 * Rendering is uniform across domains: rows are grouped by ``exception_code``
 * (stamped on every PR row by ``backend/shared/platform_reports/queries/exceptions.py``)
 * and each group renders with the config returned by
 * ``getPlatformExceptionConfig(code)`` from the central registry. The visible
 * group header uses ``row.exception_type`` (the DB-backed display label) so
 * label renames flow through without any frontend change.
 *
 * Backend column metadata (for Excel export) comes from the union at
 * ``shared/platform_reports/queries/exceptions.py::COLUMNS``.
 */
import React, { useState, useEffect, useMemo } from 'react'
import { Box, Alert, Typography, Chip } from '@mui/material'
import { useSearchParams } from 'react-router-dom'
import BaseReportPage, { ColumnDef, ExceptionTypeOption } from './BaseReportPage'
import { DataViewTable } from '../../../../components/shared/DataViewTable'
import ExceptionTypeLabel from '../../../../components/shared/ExceptionTypeLabel'
import { getPlatformExceptionConfig } from '../../../../config/exceptions/registry'
import { platformReportsService } from '../../../../services/platformReportsService'
import { buildRowInspectorLink } from '../../../../utils/urls'

const REPORT_TYPE = 'exceptions'

// Source of truth for the Domain dropdown — mirrors the admin
// /exception-options/domains endpoint so this list stays aligned with the
// admin Exception Types page. Keys are ``data_domain`` values stored in
// ``exception_types``; values are display labels.
const DOMAIN_LABELS: Record<string, string> = {
  accounting: 'Accounting',
  client: 'Client',
  product_pricing: 'Product Pricing',
  product_setup: 'Product Setup',
  sale: 'Sales',
  stock_management: 'Stock Management',
  system: 'System',
}

// Only used by BaseReportPage for its internal sort + fullscreen; the
// per-domain render paths use their own column sets.
const baseColumns: ColumnDef[] = [
  { key: 'fp_code', label: 'FP Code', link: { builder: buildRowInspectorLink, external: false } },
  { key: 'fp_name', label: 'Franchise Name' },
  { key: 'last_sync_at', label: 'Data Age', type: 'sync_status' },
  { key: 'fp_status', label: 'FP Status' },
  { key: 'exception_type', label: 'Exception Type' },
]

function renderGroupedResults(data: Record<string, any>[]): React.ReactNode {
  // Group rows by exception_code; one table per code, rendered with the
  // config from the registry. Header label uses the row's exception_type
  // (DB-backed display label).
  const groups = new Map<string, Record<string, any>[]>()
  for (const row of data) {
    const code = row.exception_code
    if (!code) continue
    if (!groups.has(code)) groups.set(code, [])
    groups.get(code)!.push(row)
  }
  if (groups.size === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="warning">
          Result rows are missing the <code>exception_code</code> field — the
          backend dispatcher should stamp this on every row.
        </Alert>
      </Box>
    )
  }
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, p: 2 }}>
      {Array.from(groups.entries()).map(([code, rows]) => {
        const config = getPlatformExceptionConfig(code)
        if (!config) {
          return (
            <Alert key={code} severity="warning">
              No platform config registered for exception code <code>{code}</code>.
            </Alert>
          )
        }
        const headerLabel = rows[0]?.exception_type || code
        return (
          <DataViewTable
            key={code}
            config={config}
            data={rows}
            variant="platform"
            exceptionTypeName={headerLabel}
            leftSlot={
              <>
                {/* Platform-report rows carry the classification under
                    `exception_classification` (not `exception_type`, which the
                    executors already use for the human display label). Same
                    concept, deliberately different field name - see
                    backend/shared/platform_reports/queries/exceptions.py. */}
                <ExceptionTypeLabel
                  exceptionType={rows[0]?.exception_classification}
                  iconSize={14}
                />
                <Typography variant="subtitle1" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                  {headerLabel}
                </Typography>
                <Chip
                  label={rows.length}
                  size="small"
                  sx={{ height: 22, fontSize: '0.75rem' }}
                />
              </>
            }
          />
        )
      })}
    </Box>
  )
}

export default function ExceptionsReport() {
  const [exceptionTypeOptions, setExceptionTypeOptions] = useState<ExceptionTypeOption[]>([])
  const [searchParams] = useSearchParams()

  useEffect(() => {
    platformReportsService.listReportTypes().then(({ reportTypes }) => {
      const report = reportTypes.find(t => t.id === REPORT_TYPE)
      if (report?.exception_types) {
        setExceptionTypeOptions(report.exception_types)
      }
    })
  }, [])

  // The single selected exception code (null when 0 or >1 selected). Derived
  // from the URL, which the Exception Type dropdown writes to, so the heading,
  // tab title, and favourite star all track the current selection. An
  // exception is favourited per type (id ``exception-<code>``), matching the
  // hub rows, so the star only appears when exactly one type is in view.
  const singleSelectedCode = useMemo(() => {
    const raw = searchParams.get('exceptionTypes')
    if (!raw) return null
    const codes = raw.split(',').filter(Boolean)
    return codes.length === 1 ? codes[0] : null
  }, [searchParams])

  const selectedExceptionLabel = useMemo(() => {
    if (!singleSelectedCode) return null
    const match = exceptionTypeOptions.find(opt => opt.key === singleSelectedCode)
    return match?.label ?? null
  }, [singleSelectedCode, exceptionTypeOptions])

  // Default "Exceptions"; extend with the selected type's short label so the
  // heading (and the star sitting beside it) name what's in view. The
  // BaseReportPage title also drives the breadcrumb + browser-tab title.
  const dynamicTitle = selectedExceptionLabel
    ? `Exceptions - ${selectedExceptionLabel}`
    : 'Exceptions'

  const renderResults = (data: Record<string, any>[]): React.ReactNode => {
    if (!data.length) return null
    return renderGroupedResults(data)
  }

  return (
      <BaseReportPage
      reportType={REPORT_TYPE}
      title={dynamicTitle}
      favouriteId={singleSelectedCode ? `exception-${singleSelectedCode}` : null}
      favouriteLabel={selectedExceptionLabel ?? undefined}
      description="Drill into any exception type across sales, stock, product, and system areas"
      filterConfig={{
        showFpSelector: true,
        showFpStatus: true,
        showCountry: true,
        showRegion: true,
        showExceptionTypes: true,
        exceptionTypeOptions,
        exceptionTypeSingleSelect: true,
        showExceptionDomainFilter: true,
        exceptionDomainLabels: DOMAIN_LABELS,
        linkFpStatusToSearch: true,
      }}
      columns={baseColumns}
      defaultSortColumn="fp_code"
      defaultSortDirection="asc"
      enableBulkActions
      renderResults={(data) => renderResults(data)}
    />
  )
}
